import * as Sentry from '@sentry/react';
import axios, { AxiosInstance } from 'axios';
import { DateTime } from 'luxon';

import { router } from '@/configs/routes';
import { routerUrls } from '@/configs/routes/routerUrls';
import { ViewTable } from '@/configs/ViewTable';
import { User } from '@/types/user';
import { Log, UsersResponse, YandexTask, YandexTaskInfo, YandexUser, YLog } from '@/types/yandex';
import { getLegacyLoginUrl } from '@/utils/legacyLoginUrl';
import { prefixToYandex } from '@/utils/prefixToYandex';

import { apiUrls } from './apiUrls';

class YandexTrackerApi {
  private baseURL = import.meta.env.VITE_API_URL;
  private axiosInstance: AxiosInstance;
  private orgId = import.meta.env.VITE_YANDEX_ORG_ID;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this._handleUnauthorized(error.config?.url);
        } else {
          Sentry.captureException(error, {
            tags: {
              api: 'yandex-tracker',
              status: error.response?.status,
            },
            extra: {
              url: error.config?.url,
              method: error.config?.method,
              responseData: error.response?.data,
            },
          });
        }

        return Promise.reject(error);
      },
    );
  }

  // Ручкам Трекера нужен токен Яндекса: и без сессии, и с истёкшим токеном — вход токеном
  // с возвратом на текущую страницу. 401 на /users/me — просто нет сессии, на вход уведёт PrivateOutlet
  private _handleUnauthorized(url?: string) {
    if (url === apiUrls.users.me || window.location.pathname.startsWith(routerUrls.login)) return;

    router.navigate(getLegacyLoginUrl(window.location.pathname + window.location.search));
  }

  async loginByToken(accessToken: string): Promise<void> {
    await this.axiosInstance.post(apiUrls.auth.loginByToken, {
      provider: 'yandex',
      access_token: accessToken,
    });
  }

  async logout(): Promise<void> {
    await this.axiosInstance.post(apiUrls.auth.logout);
  }

  async getMe(signal?: AbortSignal): Promise<{ item: User }> {
    const response = await this.axiosInstance.get(apiUrls.users.me, {
      signal,
    });

    return response.data;
  }

  async searchTasks(query: string = 'assignee: me() AND status: open'): Promise<YandexTask[]> {
    const response = await this.axiosInstance.post(prefixToYandex('issues/_search'), { query });

    return response.data;
  }

  async getFavoriteTasks(): Promise<YandexTaskInfo[]> {
    const response = await this.axiosInstance.post(prefixToYandex('v3/issues/_search'), {
      query: '"Favorited by": me()',
    });

    return response.data;
  }

  // Поиск всех доступных задач (не только назначенных пользователю)
  // Использует API suggest, который специально предназначен для автокомплита
  async searchAllTasks(searchText?: string, signal?: AbortSignal): Promise<YandexTask[]> {
    if (!searchText || !searchText.trim()) {
      return []; // Возвращаем пустой массив если нет поискового запроса
    }

    const encodedInput = encodeURIComponent(searchText.trim());

    const response = await this.axiosInstance.get(
      prefixToYandex(`v3/issues/_suggest?input=${encodedInput}&full=true`),
      { signal },
    );

    return response.data;
  }

  async getTask({
    taskKey,
    signal,
  }: {
    taskKey: string;
    signal?: AbortSignal;
  }): Promise<YandexTaskInfo> {
    const response = await this.axiosInstance.get(prefixToYandex(`v3/issues/${taskKey}`), {
      signal,
    });

    return response.data;
  }

  async createWorklog({
    taskKey,
    duration,
    comment,
    startDate,
    userIds,
    eventUid,
    eventRecurrenceId,
  }: {
    taskKey: string;
    duration: string;
    comment: string;
    startDate?: string;
    userIds?: string[];
    eventUid?: string;
    eventRecurrenceId?: string | null;
  }): Promise<{ items: Log[] }> {
    const workLogData: Record<string, unknown> = {
      start: startDate || new Date().toISOString(),
      duration,
      comment,
      userIds,
    };

    if (eventUid) {
      workLogData.eventUid = eventUid;
    }

    if (eventRecurrenceId) {
      workLogData.eventRecurrenceId = eventRecurrenceId;
    }

    const response = await this.axiosInstance.post(
      apiUrls.tasks.createWorklog(taskKey),
      workLogData,
    );

    return response.data;
  }

  async getUsers({
    q,
    showAll,
    signal,
  }: {
    q?: string;
    showAll?: boolean;
    signal?: AbortSignal;
  }): Promise<UsersResponse> {
    const params: Record<string, string | boolean> = {};

    if (q) {
      params.q = q;
    }

    if (showAll) {
      params.show_all = true;
    }

    const response = await this.axiosInstance.get(apiUrls.users.suggest, {
      params,
      signal,
    });

    return response.data;
  }

  async getWorkLogs({
    taskKey,
    signal,
  }: {
    taskKey: string;
    signal?: AbortSignal;
  }): Promise<YLog[]> {
    const allLogs: YLog[] = [];
    const perPage = 100;
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await this.axiosInstance.get(
        prefixToYandex(`v3/issues/${taskKey}/worklog`),
        {
          params: { page, perPage },
          signal,
        },
      );

      const pageLogs = Array.isArray(response.data) ? (response.data as YLog[]) : [];
      allLogs.push(...pageLogs);

      const headerPages = Number(response.headers['x-total-pages']);

      if (Number.isFinite(headerPages) && headerPages > 0) {
        totalPages = headerPages;
      } else if (pageLogs.length < perPage) {
        break;
      } else {
        totalPages = page + 1;
      }

      page += 1;
    }

    return allLogs;
  }

  async updateWorkLog({
    taskKey,
    workLogId,
    duration,
    comment,
  }: {
    taskKey: string;
    workLogId: string;
    duration: string;
    comment?: string;
  }): Promise<YLog> {
    const workLogData: Record<string, string> = {
      duration, // Обязательный параметр
    };

    // Добавляем комментарий только если он предоставлен
    if (comment) {
      workLogData.comment = comment;
    }

    const response = await this.axiosInstance.patch(
      prefixToYandex(`v3/issues/${taskKey}/worklog/${workLogId}`),
      workLogData,
    );

    return response.data;
  }

  async deleteWorkLog({
    taskKey,
    workLogId,
  }: {
    taskKey: string;
    workLogId: string;
  }): Promise<void> {
    await this.axiosInstance.delete(prefixToYandex(`/v3/issues/${taskKey}/worklog/${workLogId}`));
  }

  // Получить все логи времени пользователя за период (правильный способ)
  async getAllUserWorkLogs({
    createdBy,
    from,
    to,
    signal,
    view,
  }: {
    createdBy: string;
    from?: string;
    to?: string;
    signal?: AbortSignal;
    view?: ViewTable;
  }): Promise<YLog[]> {
    const searchBody = {
      createdBy: createdBy,
      start: {
        from: from,
        to: to,
      },
    };

    const response = await this.axiosInstance.post(
      prefixToYandex('v3/worklog/_search'),
      searchBody,
      {
        headers: {
          'x-org-id': this.orgId,
        },
        params: {
          perPage: view === ViewTable.Week ? '200' : '1000',
        },
        signal,
      },
    );

    return response.data;
  }

  // Получить все логи времени пользователя за неделю
  async getWeeklyWorkLogs({
    createdBy,
    weekStart,
    signal,
  }: {
    createdBy: string;
    weekStart: string;
    signal?: AbortSignal;
  }): Promise<YLog[]> {
    const startDate = DateTime.fromISO(weekStart).setLocale('ru').startOf('day');
    const endDate = startDate.plus({ days: 6 }).endOf('day');
    const from = startDate.toLocal().toISO()!;
    const to = endDate.toLocal().toISO()!;

    return this.getAllUserWorkLogs({ createdBy, from, to, signal });
  }

  // Получение списка пользователей и групп
  async getUsersAndGroups(): Promise<YandexUser[]> {
    const response = await this.axiosInstance.get(prefixToYandex('v3/users'), {
      headers: {
        'x-org-id': this.orgId,
      },
    });

    if (response.status !== 200) {
      throw new Error('Failed to load users');
    }

    return response.data;
  }

  async getUserById(id: string): Promise<YandexUser> {
    const response = await this.axiosInstance.get(prefixToYandex(`v3/users/${id}`), {
      headers: {
        'x-org-id': this.orgId,
      },
    });

    if (response.status !== 200) {
      throw new Error('Failed to load users');
    }

    return response.data;
  }
}

export const yandexTrackerApi = new YandexTrackerApi();
